<template>
  <!--
    EpisodePlayerDialog.vue
    Standalone "Play Episode" quick-play dialog: plays ONE episode immediately
    (caching first if needed), independent of the playlist queue (see
    useEpisodePlayer.js's module doc for why this is a separate composable
    and component rather than a mode of MediaPlayer.vue/useMediaPlayer.js).

    Deliberately minimal compared to the playlist player: no prev/next queue
    navigation, no sleep timer, no share sheet -- just play/pause, seek, and
    close. Visually matches MediaPlayer.vue's fullscreen "glass" look for
    consistency.

    KEY LAYOUT DETAIL: the component is mounted for as long as isOpen is true
    (preparing/playing/pip -- see App.vue's v-if), and the <video> tag is
    NEVER removed for that whole span, since the browser ends PiP the moment
    its source element leaves the document. But the wrapper must NOT stay a
    fullscreen, click-blocking layer once handed off to PiP (isOpen true,
    isDialogVisible false) -- otherwise an invisible fullscreen div would
    cover the entire app while the user is supposed to be free to use it.
    So the wrapper's fullscreen/opaque presentation is a CSS class toggle
    (.chrome-visible vs .chrome-hidden), not a v-if/v-show on the wrapper
    itself: .chrome-hidden shrinks it to an inert 1x1 box (opacity/pointer-
    events/size only -- never `display:none`, which is the one CSS state
    that's ambiguous for PiP survival across browsers) so the video element
    stays genuinely attached and unaffected while occupying no visible space.
  -->
  <div
    ref="wrapperEl"
    class="episode-player-wrapper"
    :class="isDialogVisible ? 'chrome-visible' : 'chrome-hidden'"
  >
    <div
      class="video-container"
      @mousemove.passive="showControlsTemporarily"
    >
      <!-- Title bar: episode title + close button, same glass-pill style as MediaPlayer.vue.
           Fades with the controls (auto-hides while playing; tap the video to reveal). -->
      <div
        v-if="isDialogVisible"
        class="title-row"
        :class="{ 'overlay-visible': controlsShown }"
        @pointerdown.passive="showControlsTemporarily"
      >
        <div class="title-bar">
          <span class="episode-title text-wrap">{{ episode?.title || episode?.episode_title }}</span>
        </div>
        <v-btn
          icon
          size="40"
          variant="text"
          class="close-btn-detached"
          aria-label="Close player"
          @click.stop="handleClose"
        >
          <v-icon size="26" color="white">mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Video element -- src only set once resolvedVideoUrl is ready (cached).
           No native controls; our own controls bar below handles seek/play/pause. -->
      <video
        ref="videoEl"
        :src="resolvedVideoUrl"
        class="video-player"
        @timeupdate="onTimeUpdate"
        @loadedmetadata="onLoadedMetadata"
        @seeked="onSeeked"
        @ended="onEnded"
        @error="onError"
        @playing="onPlaying"
        @pause="onPause"
        @leavepictureinpicture="onLeavePiP"
        @webkitbeginfullscreen="isFullscreen = true"
        @webkitendfullscreen="isFullscreen = false"
        @click.prevent="showControlsTemporarily"
        preload="metadata"
        playsinline
        webkit-playsinline
        x5-playsinline
      />

      <!-- Audio episodes: show the episode's image over the otherwise-black
           <video> surface, same component as the playlist player. Only while
           the dialog is visible — handed off to PiP there's nothing to see. -->
      <AudioArtwork
        v-if="isAudio && isDialogVisible"
        :thumbnail="episode?.thumbnail_path"
      />

      <!-- Center play/pause overlay -- shown whenever paused (tap the video to toggle). -->
      <div
        v-if="isDialogVisible"
        class="play-overlay"
        :class="{ 'overlay-visible': !isPlaying && !error && phase === 'playing' }"
      >
        <v-btn
          icon
          variant="flat"
          size="80"
          class="glass-circle glass-circle-primary"
          :aria-label="isPlaying ? 'Pause' : 'Play'"
          @click.stop="togglePlayPause"
        >
          <v-icon size="48" color="white">{{ isPlaying ? "mdi-pause" : "mdi-play" }}</v-icon>
        </v-btn>
      </div>

      <!-- Preparing/caching overlay -- same visual language as MediaPlayer.vue's,
           shown while an uncached episode is being downloaded, or on error
           (with a Close button instead of an endless spinner). -->
      <div
        v-if="phase === 'preparing'"
        class="preparing-overlay d-flex flex-column align-center justify-center text-center ga-4 pa-6"
      >
        <template v-if="error">
          <v-icon icon="mdi-alert-circle-outline" color="error" size="64"></v-icon>
          <div class="text-body-1 font-weight-medium text-white">Couldn't play this episode</div>
          <div class="text-body-2 text-grey-lighten-1" style="max-width: 32rem">{{ error }}</div>
          <v-btn variant="tonal" color="white" @click="handleClose">Close</v-btn>
        </template>
        <template v-else>
          <v-progress-circular
            :model-value="cachingProgress || 0"
            :indeterminate="!cachingProgress"
            color="white"
            size="72"
            width="6"
          >
            <span v-if="cachingProgress" class="text-caption font-weight-bold text-white"
              >{{ Math.ceil(cachingProgress) }}%</span
            >
          </v-progress-circular>
          <div class="text-body-1 font-weight-medium text-white">{{ cachingStatusMessage }}</div>
        </template>
      </div>

      <!-- Playback error after a successful cache+load (e.g. corrupted file, codec issue). -->
      <div
        v-if="phase === 'playing' && error"
        class="preparing-overlay d-flex flex-column align-center justify-center text-center ga-4 pa-6"
      >
        <v-icon icon="mdi-alert-circle-outline" color="error" size="64"></v-icon>
        <div class="text-body-1 font-weight-medium text-white">Playback error</div>
        <div class="text-body-2 text-grey-lighten-1" style="max-width: 32rem">{{ error }}</div>
        <v-btn variant="tonal" color="white" @click="handleClose">Close</v-btn>
      </div>

      <!-- Controls bar: seek slider + time labels + skip/fullscreen. Auto-hides
           after a few seconds of no interaction while playing (tap the video to
           reveal); stays visible while paused. -->
      <div
        v-if="phase === 'playing' && !error"
        class="controls-bar"
        :class="{ 'overlay-visible': controlsShown }"
        @pointerdown.passive="showControlsTemporarily"
      >
        <div class="slider-row">
          <span class="time-display ml-1">{{ formatTime(currentTime) }}</span>
          <v-slider
            :model-value="currentTime"
            :max="duration || 1"
            hide-details
            color="white"
            track-color="rgba(255,255,255,0.3)"
            class="seek-slider"
            @update:model-value="onSeek"
          ></v-slider>
          <span class="time-display mr-1">{{ formatTime(duration) }}</span>
        </div>
        <div class="controls-actions">
          <v-btn icon variant="text" size="44" class="control-icon-btn" aria-label="Skip backward 10 seconds" @click="skipBackward">
            <v-icon size="24">mdi-rewind-10</v-icon>
          </v-btn>
          <v-btn icon variant="text" size="44" class="control-icon-btn" :aria-label="isPlaying ? 'Pause' : 'Play'" @click="togglePlayPause">
            <v-icon size="28">{{ isPlaying ? "mdi-pause" : "mdi-play" }}</v-icon>
          </v-btn>
          <v-btn icon variant="text" size="44" class="control-icon-btn" aria-label="Skip forward 30 seconds" @click="skipForward">
            <v-icon size="24">mdi-fast-forward-30</v-icon>
          </v-btn>
          <!-- Episode info: the same "Episode info" sheet the playlist player's
               (i) opens (EpisodeInfoSheet.vue). Shown for audio and video alike —
               requested for the audio player, where there was no way to read
               the description while listening. -->
          <v-btn icon variant="text" size="44" class="control-icon-btn" aria-label="Episode info" @click="infoSheetOpen = true">
            <v-icon size="26">mdi-information-outline</v-icon>
          </v-btn>
          <!-- Picture-in-Picture (video only; hidden when the browser lacks PiP
               support). Same icon, order and placement as the playlist player. -->
          <v-btn
            v-if="!isAudio && supportsPiP"
            icon
            variant="text"
            size="44"
            class="control-icon-btn"
            aria-label="Picture in picture"
            @click="togglePiP()"
          >
            <v-icon size="26">mdi-picture-in-picture-bottom-right</v-icon>
          </v-btn>

          <!-- Fullscreen toggle (video only — pointless for audio episodes),
               same behavior as the playlist player: desktop fullscreens the
               wrapper so these controls stay, mobile fullscreens the video. -->
          <v-btn
            v-if="!isAudio"
            icon
            variant="text"
            size="44"
            class="control-icon-btn"
            :aria-label="isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'"
            @click="toggleFs"
          >
            <v-icon size="26">{{ isFullscreen ? "mdi-fullscreen-exit" : "mdi-fullscreen" }}</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- The (i) button's sheet. Only while the dialog is visible — handed off
         to Picture-in-Picture there is no dialog to show it over. -->
    <EpisodeInfoSheet
      v-if="isDialogVisible"
      v-model="infoSheetOpen"
      :episode="episode"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { useDisplay } from "vuetify";
import { useEpisodePlayer } from "@/composables/useEpisodePlayer.js";
import { useMediaPlayer } from "@/composables/useMediaPlayer.js";
// resetDownload: libera la cache a fine ascolto, se l'impostazione è attiva.
import { episodesApi } from "@/api/episodes.js";
import { useHistory } from "@/composables/useHistory.js";
// Cover-art layer for audio episodes (no video frame to show otherwise)
import AudioArtwork from "@/components/AudioArtwork.vue";
// The (i) button's "Episode info" sheet, shared with the playlist player
import EpisodeInfoSheet from "@/components/EpisodeInfoSheet.vue";
import {
  toggleFullscreen as toggleVideoFullscreen,
  isFullscreenActive,
} from "@/services/fullscreen.js";
import {
  supportsPiP as videoSupportsPiP,
  togglePiP as toggleVideoPiP,
} from "@/services/pictureInPicture.js";

// Standalone quick-play state (phase/episode/resolvedVideoUrl/caching overlay/error)
const {
  phase,
  episode,
  resolvedVideoUrl,
  cachingProgress,
  cachingStatusMessage,
  error,
  isDialogVisible,
  close,
  teardown,
  handlePlaybackError,
} = useEpisodePlayer();

// Reused from the playlist player: ID-keyed position helpers + global playback
// prefs (volume/skip-intro/outro settings) -- NOT its queue/current-episode state.
const {
  getEpisodePosition,
  setEpisodePosition,
  skipIntroDuration,
  skipOutroDuration,
  skipIntroEnabled,
  skipOutroEnabled,
  volume,
  formatTime,
} = useMediaPlayer();

// Play History recording -- same fire-and-forget pattern MediaPlayer.vue uses.
const { recordPlay, recordListened } = useHistory();

// Small/touch viewport → fullscreen the <video> (iOS); desktop → the wrapper.
const { mobile } = useDisplay();

const wrapperEl = ref(null); // The fullscreen target on desktop
const videoEl = ref(null); // The <video> DOM element
const currentTime = ref(0);
const duration = ref(0);
const isPlaying = ref(false);
// Whether we're in native fullscreen (drives the button's icon/label). Kept in
// sync by the document fullscreenchange listener + the iOS webkit video events.
const isFullscreen = ref(false);
// Audio episodes have no video surface — hide the fullscreen control for them.
const isAudio = computed(() => episode.value?.episode_type === "audio");
// Whether the (i) "Episode info" sheet is open.
const infoSheetOpen = ref(false);

// Whether the browser can do PiP for this video element. Same shared util the
// playlist player uses, so both agree on what's supported (standard API +
// iOS/WebKit's webkitSetPresentationMode).
const supportsPiP = computed(() => videoSupportsPiP(videoEl.value));

/**
 * togglePiP - Enters or exits Picture-in-Picture, leaving the dialog open.
 *
 * NOTE this is a genuine toggle, unlike the X button's PiP HANDOFF (which pops
 * the video out and dismisses the dialog — see useEpisodePlayer.js's close()).
 * Both end up in the OS PiP window, but they differ in what leaving PiP means,
 * which is exactly what onLeavePiP below keys off `phase` to tell apart.
 */
const togglePiP = () => {
  toggleVideoPiP(videoEl.value).catch((err) => {
    console.error("PiP toggle failed:", err);
  });
};

// ── Controls auto-hide ──────────────────────────────────────────────────────
// The controls (title bar + bottom controls bar) fade out after a few seconds
// of no interaction WHILE PLAYING, and reappear on tap/mouse-move — the video-
// player convention this dialog was missing (controls previously stayed forever
// on mobile). While paused they're always shown so the play button is reachable.
const overlayVisible = ref(true);
let overlayTimeout = null;
const CONTROLS_HIDE_MS = 3000;
// Controls are shown while overlayVisible is true OR whenever playback is paused.
const controlsShown = computed(() => overlayVisible.value || !isPlaying.value);
// Show the controls and (re)arm the auto-hide countdown. The hide only fires if
// still playing when it elapses, so paused controls never disappear.
const showControlsTemporarily = () => {
  overlayVisible.value = true;
  if (overlayTimeout) clearTimeout(overlayTimeout);
  overlayTimeout = setTimeout(() => {
    if (isPlaying.value) overlayVisible.value = false;
  }, CONTROLS_HIDE_MS);
};
// Guards onTimeUpdate from clobbering a just-restored position before the
// browser's `seeked` event fires -- same technique MediaPlayer.vue uses.
const isRestoringPosition = ref(false);
// Whether this episode's intro/outro have already been skipped this session
// (local to this dialog -- NOT useMediaPlayer's introSkipped/outroSkipped,
// which belong to the queue player's own currently-loaded episode).
const introSkipped = ref(false);
const outroSkipped = ref(false);

// Listened-time accumulation: same pattern as MediaPlayer.vue's flushListened
// (small forward deltas only, so seeks/rewinds aren't counted; flushed on
// pause, periodically, and on teardown).
let listenAccumSec = 0;
let listenPrevTime = null;
let listenFlushInterval = null;
let lastRecordedPlayId = null;

const flushListened = () => {
  const secs = Math.floor(listenAccumSec);
  if (secs >= 1 && episode.value) {
    listenAccumSec -= secs;
    recordListened(episode.value.id, secs);
  }
};

const onTimeUpdate = () => {
  if (!videoEl.value || isRestoringPosition.value) return;
  const newTime = videoEl.value.currentTime;
  currentTime.value = newTime;
  if (episode.value) setEpisodePosition(episode.value.id, newTime);

  if (listenPrevTime != null) {
    const dt = newTime - listenPrevTime;
    if (dt > 0 && dt < 2.5) listenAccumSec += dt;
  }
  listenPrevTime = newTime;

  // Skip intro: jump past the first N seconds once, same rule the playlist
  // player uses (enabled + duration configured + not already skipped).
  if (skipIntroEnabled.value && skipIntroDuration.value > 0 && !introSkipped.value && newTime < skipIntroDuration.value) {
    introSkipped.value = true;
    videoEl.value.currentTime = skipIntroDuration.value;
  }

  // Skip outro: once within N seconds of the end, end the session (no
  // "next episode" to advance to here -- this is a single standalone play).
  if (
    skipOutroEnabled.value &&
    skipOutroDuration.value > 0 &&
    !outroSkipped.value &&
    duration.value > 0 &&
    newTime >= duration.value - skipOutroDuration.value
  ) {
    outroSkipped.value = true;
    onEnded();
  }
};

const onLoadedMetadata = () => {
  if (!videoEl.value || !episode.value) return;
  duration.value = videoEl.value.duration;
  const savedPosition = getEpisodePosition(episode.value.id);
  if (savedPosition > 0) {
    isRestoringPosition.value = true;
    videoEl.value.currentTime = savedPosition;
    currentTime.value = savedPosition;
    setTimeout(() => {
      isRestoringPosition.value = false;
    }, 1000);
  }
  videoEl.value.volume = volume.value;
  videoEl.value.play().catch(() => {
    // Autoplay blocked by browser policy -- the center play button lets the
    // user start it manually; no error state needed for this case.
  });
};

const onSeeked = () => {
  isRestoringPosition.value = false;
};

const onSeek = (value) => {
  if (!videoEl.value) return;
  videoEl.value.currentTime = value;
  currentTime.value = value;
  showControlsTemporarily(); // keep controls up while scrubbing
};

const onPlaying = () => {
  isPlaying.value = true;
  showControlsTemporarily(); // arm the auto-hide once playback actually starts
  const epId = episode.value?.id;
  if (epId != null && epId !== lastRecordedPlayId) {
    lastRecordedPlayId = epId;
    recordPlay(epId);
  }
};

const onPause = () => {
  isPlaying.value = false;
  listenPrevTime = null;
  flushListened();
  // Keep controls visible while paused (controlsShown is true via !isPlaying);
  // cancel any pending auto-hide so they don't blink off.
  if (overlayTimeout) clearTimeout(overlayTimeout);
};

const onError = () => {
  isPlaying.value = false;
  // The video failed to load/play. This can genuinely happen even though
  // quickPlay thought the episode was already cached (stale cached_at --
  // e.g. the file was cleaned up since this episode's list data was last
  // fetched) -- handlePlaybackError re-checks the server's live status and
  // falls back into the caching/poll flow if it's actually still downloading.
  handlePlaybackError();
};

const togglePlayPause = () => {
  if (!videoEl.value) return;
  if (videoEl.value.paused) {
    videoEl.value.play().catch(() => {});
  } else {
    videoEl.value.pause();
  }
};

const skipBackward = () => {
  if (!videoEl.value) return;
  videoEl.value.currentTime = Math.max(0, videoEl.value.currentTime - 10);
};

const skipForward = () => {
  if (!videoEl.value) return;
  videoEl.value.currentTime = Math.min(duration.value || Infinity, videoEl.value.currentTime + 30);
};

// Fullscreen toggle — delegates to the shared cross-browser util.
const toggleFs = () => {
  toggleVideoFullscreen({
    videoEl: videoEl.value,
    wrapperEl: wrapperEl.value,
    isMobile: mobile.value,
  });
};

// Keep isFullscreen in sync with the browser (covers Esc / the native exit
// control on desktop & Android; iOS uses the video's webkit events bound in
// the template). Registered on mount, removed on unmount.
const onFullscreenChange = () => {
  isFullscreen.value = isFullscreenActive();
};
onMounted(() => {
  document.addEventListener("fullscreenchange", onFullscreenChange);
  document.addEventListener("webkitfullscreenchange", onFullscreenChange);
});
onUnmounted(() => {
  document.removeEventListener("fullscreenchange", onFullscreenChange);
  document.removeEventListener("webkitfullscreenchange", onFullscreenChange);
  if (overlayTimeout) clearTimeout(overlayTimeout);
});

// Natural end: flush listened time, then fully tear down (reset saved
// position -- the episode played all the way through). Never attempts PiP;
// there's nothing left to keep playing.
const onEnded = () => {
  flushListened();
  teardown({ natural: true });
};

// Native PiP ended -- whichever way (user closed the floating window, its
// "back to tab" control, or exitPictureInPicture() was called somewhere).
// There's no scriptable way to tell those apart, so `phase` decides:
//
//   phase === 'pip'  The X button HANDED OFF to PiP and dismissed the dialog,
//                    so the floating window was the whole session. Leaving it
//                    means done -- tear down (this is the original behaviour).
//
//   otherwise        The PiP button was toggled while the dialog stayed open
//                    (phase is still 'playing'). Leaving PiP just returns the
//                    video inline; ending the session here would kill playback
//                    on a plain toggle-off, which is not what the control means.
const onLeavePiP = () => {
  if (phase.value !== "pip") return;
  flushListened();
  teardown({ videoEl: videoEl.value, currentTime: currentTime.value });
};

// Close (X) button -- hands off to PiP if eligible, otherwise fully closes.
// See useEpisodePlayer.js's close() for the exact eligibility rules.
const handleClose = () => {
  flushListened();
  close({ videoEl: videoEl.value, currentTime: currentTime.value });
};

// Reset local per-session state whenever a new episode starts (quickPlay
// clears phase/episode synchronously; this watch catches that transition).
watch(episode, (newEp, oldEp) => {
  if (newEp?.id !== oldEp?.id) {
    // A new episode never opens with the previous one's info sheet showing.
    infoSheetOpen.value = false;
    currentTime.value = 0;
    duration.value = 0;
    isPlaying.value = false;
    introSkipped.value = false;
    outroSkipped.value = false;
    listenAccumSec = 0;
    listenPrevTime = null;
    lastRecordedPlayId = null;
    if (!listenFlushInterval) {
      listenFlushInterval = setInterval(flushListened, 15000);
    }
  }
  if (!newEp && listenFlushInterval) {
    clearInterval(listenFlushInterval);
    listenFlushInterval = null;
  }
});
</script>

<style scoped>
/* Visual language copied from MediaPlayer.vue's "glass" look for consistency
   between the two players -- CSS is scoped per-component so it can't be
   imported/shared the way the pure-JS utils were; duplicating a handful of
   static style rules is low-risk compared to duplicating logic. */

.episode-player-wrapper {
  display: flex;
  flex-direction: column;
  position: fixed;
}

/* Normal state: fullscreen black player, same as MediaPlayer.vue's wrapper. */
.episode-player-wrapper.chrome-visible {
  background: #000;
  inset: 0;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  z-index: 2000;
  overflow: hidden;
}

/* Handed off to Picture-in-Picture: shrink to an inert 1x1 box instead of
   `display:none`, so the <video> element stays genuinely attached and
   unaffected while it occupies no visible space and blocks no clicks. */
.episode-player-wrapper.chrome-hidden {
  top: 0;
  left: 0;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
  z-index: -1;
}

.video-container {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #000;
  overflow: hidden;
  min-height: 0;
  position: relative;
}

.video-player {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
}

.title-row {
  position: absolute;
  top: 12px;
  left: 8px;
  right: 8px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  z-index: 10;
  /* Fades with the controls (see controlsShown). */
  opacity: 0;
  transition: opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
}
.title-row.overlay-visible {
  opacity: 1;
  pointer-events: auto;
}

.title-bar {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  padding: 10px 16px;
  border-radius: 28px;
  background: rgba(28, 28, 30, 0.45);
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.14);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.22),
    inset 0 -1px 0 rgba(0, 0, 0, 0.2),
    0 8px 30px rgba(0, 0, 0, 0.45);
}

.close-btn-detached {
  flex-shrink: 0;
  border-radius: 28px;
  background: rgba(28, 28, 30, 0.45) !important;
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.14);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.22),
    inset 0 -1px 0 rgba(0, 0, 0, 0.2),
    0 8px 30px rgba(0, 0, 0, 0.45);
}

.episode-title {
  font-size: 0.85rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.95);
  flex: 1;
  min-width: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 5;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
}

.play-overlay.overlay-visible {
  opacity: 1;
  pointer-events: auto;
}

:deep(.glass-circle) {
  background: rgba(28, 28, 30, 0.38) !important;
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.16);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.2),
    0 6px 22px rgba(0, 0, 0, 0.4);
}

:deep(.glass-circle-primary) {
  background: rgba(255, 255, 255, 0.22) !important;
}

.preparing-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 6;
}

.controls-bar {
  position: absolute;
  bottom: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  z-index: 10;
  /* Fades with the controls (see controlsShown). */
  opacity: 0;
  transition: opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
}
.controls-bar.overlay-visible {
  opacity: 1;
  pointer-events: auto;
}

.slider-row {
  width: 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  min-height: 56px;
  border-radius: 28px;
  background: rgba(28, 28, 30, 0.45);
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.14);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.22),
    inset 0 -1px 0 rgba(0, 0, 0, 0.2),
    0 8px 30px rgba(0, 0, 0, 0.45);
}

.controls-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 4px 10px;
  min-height: 56px;
  width: 100%;
  border-radius: 28px;
  background: rgba(28, 28, 30, 0.45);
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.28);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.22),
    inset 0 -1px 0 rgba(0, 0, 0, 0.2),
    0 8px 30px rgba(0, 0, 0, 0.45);
}

.control-icon-btn {
  color: #fff !important;
}

.time-display {
  font-size: 0.75rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  min-width: 40px;
  text-align: center;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
}

.seek-slider {
  flex: 1;
  min-width: 0;
  margin: 0 2px;
}
</style>
